import React, { useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type WebView from 'react-native-webview';
import { HostedFrame, theme } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import { FRAME_HEIGHT, FRAME_ID } from './constants';
import { getApplePaySource } from './getApplePaySource';
import { useApplePayMessages } from './useApplePayMessages';

export const ApplePayButton = (props: ApplePayButtonProps) => {
  const { stripeKey, clientSecret, amountToCapture, onCapturePaymentMethodId } =
    props;

  const webViewRef = useRef<WebView>(null);
  const { t } = useLocalizationContext();

  const applePaySource = useMemo(
    () =>
      getApplePaySource({
        stripeKey,
        clientSecret,
        amountToCapture,
      }),
    [amountToCapture, clientSecret, stripeKey],
  );

  const { isApplePayEnabled, handleApplePayMessages } = useApplePayMessages({
    onCapturePaymentMethodId,
  });

  const style = [styles.content, isApplePayEnabled ? null : styles.loading];

  if (!stripeKey || !clientSecret) {
    return null;
  }

  return (
    <HostedFrame
      ref={webViewRef}
      frameId={FRAME_ID}
      frameHeight={isApplePayEnabled ? FRAME_HEIGHT : 0}
      title={t('general.apple-pay')}
      source={applePaySource}
      style={style}
      containerStyle={style}
      onMessage={handleApplePayMessages}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ApplePayButtonProps = Readonly<{
  stripeKey: string;
  clientSecret: string;
  amountToCapture: number;
  onCapturePaymentMethodId?: (props: {
    id: string;
    nickname: string;
    isDefault: boolean;
  }) => Promise<void>;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loading: {
    backgroundColor: theme.colors.OATMEAL,
    height: 0,
  },
  content: {
    backgroundColor: theme.colors.OATMEAL,
    marginVertical: theme.spacing['1'],
    height: FRAME_HEIGHT,
  },
});
